import { useEffect, useSyncExternalStore } from "react"

// THE ONE PLACE that controls which in-house popup may be on screen.
//
// Rules, enforced for every auto-opening popup that calls useModalSlot:
//   1. Only ONE popup is ever visible at a time. Others wait their turn.
//   2. When several want the screen, the lowest number below goes first.
//   3. A short gap separates one popup closing and the next opening.
//   4. At most MAX_COUNTED_PER_VISIT popups per page load (the age gate is
//      exempt). Anything past the cap stays unseen and shows on a later visit,
//      so nobody gets a chain of 4 popups after signing up.
// A popup only counts as "seen" when the user dismisses it, so a popup that
// waited and never got a slot is NOT lost.
//
// To add a popup: add an id here, then in the component:
//   const canShow = useModalSlot("yourId", wantsToShow)
//   ...and render nothing unless canShow.
export const MODAL_PRIORITY = {
  ageGate: 0, // legal gate: blocks everything until passed
  dailyClaim: 5, // "+N Gooncoin, daily login bonus" confirmation (exempt from the cap)
  onboardingLanguage: 11, // off by default (language is auto-detected + in Settings)
  goonpalsBeta: 15, // tied to the page the user deliberately opened (exempt from the cap)
  dayOnePromos: 20, // Early Access + Glymtober (one stack, shown in sequence)
  badgeUnlock: 30, // something the user earned
  swipeTutorial: 35, // one-time hint for the next-tag swipe, after the user has two tag searches
  patchNotes: 40,
  founderPromo: 50,
  founderPlus: 51,
  promo: 60,
} as const

export type ModalId = keyof typeof MODAL_PRIORITY

const MAX_COUNTED_PER_VISIT = 2
const GAP_MS = 800
// Still one-at-a-time and in priority order, but never dropped by the cap.
const CAP_EXEMPT: ReadonlySet<ModalId> = new Set<ModalId>(["ageGate", "dailyClaim", "goonpalsBeta"])

type Entry = { id: ModalId; since: number }

let entries: Entry[] = []
let activeId: ModalId | null = null
let grantedCount = 0
let lastReleaseAt = 0
let gapTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((listener) => listener())
}

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

function schedule() {
  if (activeId !== null) return
  const next = entries
    .filter((e) => CAP_EXEMPT.has(e.id) || grantedCount < MAX_COUNTED_PER_VISIT)
    .sort((a, b) => MODAL_PRIORITY[a.id] - MODAL_PRIORITY[b.id] || a.since - b.since)[0]
  if (!next) return
  const wait = GAP_MS - (Date.now() - lastReleaseAt)
  if (wait > 0 && next.id !== "ageGate") {
    if (!gapTimer) {
      gapTimer = setTimeout(() => {
        gapTimer = null
        schedule()
      }, wait)
    }
    return
  }
  activeId = next.id
  if (!CAP_EXEMPT.has(next.id)) grantedCount++
  emit()
}

/**
 * Returns true only while this popup holds the single on-screen slot.
 * `wants` = the popup's own logic says it should be open (after its delay,
 * eligibility and seen-state checks). Keep `wants` true for as long as the
 * popup is open; set it false when it closes so the next one can go.
 * `bypass` skips the coordinator entirely (admin previews / forced opens).
 */
export function useModalSlot(id: ModalId, wants: boolean, opts?: { bypass?: boolean }): boolean {
  const bypass = opts?.bypass ?? false

  useEffect(() => {
    if (bypass || !wants) return
    entries = [...entries.filter((e) => e.id !== id), { id, since: Date.now() }]
    schedule()
    return () => {
      entries = entries.filter((e) => e.id !== id)
      if (activeId === id) {
        activeId = null
        lastReleaseAt = Date.now()
      }
      emit()
      schedule()
    }
  }, [id, wants, bypass])

  const current = useSyncExternalStore(subscribe, () => activeId, () => null)
  return bypass || (wants && current === id)
}
